<template>
  <div class="profile-page">
    <div class="profile-page__top mb-5">
      <div class="d-flex h4 primary--text">
        {{ $t('common.dealer') }}
        <span
          v-if="dealer"
          class="pl-2"
        >
          {{ dealer.name }} / {{ dealer.asdb }}
        </span>
        <v-btn
          class="ml-auto"
          color="primary"
          outlined
          @click="$router.go(-1)"
        >
          {{ $t('common.go_back') }}
        </v-btn>
      </div>
    </div>
    <v-card
      v-if="dealer"
      class="profile-page__main mb-5"
    >
      <v-form
        @submit.prevent="save"
      >
        <div class="p-4">
          <div class="profile-page__main__item__content__item">
            <span class="profile-page__main__item__content__item__title">
              {{ $t('views.dealer_item.timezone') }}
            </span>
            <div class="input-block input-block_white mt-1">
              <multiselect
                v-model="dealer.timezone"
                :options="timezones"
                placeholder=""
                selected-label=""
                select-label=""
                deselect-label=""
              />
              <!-- <span
                v-else
                class="profile-page__main__item__content__item__text"
              >
                {{ dealer.timezone || $t('common.no_data') }}
              </span> -->
            </div>
          </div>

          <div class="profile-page__main__item__content__item">
            <span class="profile-page__main__item__content__item__title">
              {{ $t('views.dealer_item.language') }}
            </span>
            <div class="input-block input-block_white mt-1">
              <v-select
                v-model="dealer.language"
                :items="locales"
                hide-details
                solo
              />
              <!-- <span
                v-else
                class="profile-page__main__item__content__item__text"
              >
                {{ dealer.language || $t('common.no_data') }}
              </span> -->
            </div>
          </div>

          <v-btn
            color="primary"
            outlined
            type="submit"
          >
            {{ $t('common.save') }}
          </v-btn>
        </div>
      </v-form>
    </v-card>

    <template v-if="dealer">
      <div class="d-flex mb-3 align-items-end">
        <h1 class="h4 primary--text">
          {{ $t('common.contacts') }}
        </h1>
        <div class="ml-auto">
          <v-btn
            class="mr-4"
            color="primary"
            outlined
            large
            @click="showContactsModal('add')"
          >
            <v-icon left>
              mdi-plus
            </v-icon>
            {{ $t('common.add') }}
          </v-btn>
        </div>
      </div>
      <v-card class="mb-5">
        <v-data-table
          :headers="headers"
          :items="contacts"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50, 100],
          }"
          class="px-5"
        >
          <template
            v-slot:item.role="{ item }"
          >
            {{ $t(`enums.dealer_notification_roles.${item.role}`) }}
          </template>
          <template
            v-slot:item.actions="{ item }"
          >
            <v-hover v-slot="{hover}">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                @click="showContactsModal('edit', item)"
              >
                mdi-pencil
              </v-icon>
            </v-hover>
            <v-hover
              v-if="item._id"
              v-slot="{hover}"
            >
              <v-icon
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                @click="removeContact(item._id)"
              >
                mdi-trash-can-outline
              </v-icon>
            </v-hover>
          </template>
        </v-data-table>
      </v-card>
      <contacts-modal
        v-model="contactsModal"
        :contact-selected="contactSelected"
        :type="modalType"
        :dealer="dealer.asdb"
        @submit="contactsModalSubmit"
      />
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { contactRoles } from '@/utils/enums';
import ContactsModal from '@/components/profile/ContactsModal';

export default {
  name: 'DealerItem',

  components: {
    ContactsModal,
  },

  props: {
    asdb: {
      type: [String, Object],
      default: null,
    },
  },

  data: () => ({
    dealer: null,
    contacts: [],
    contactsModal: false,
    contactSelected: null,
    modalType: 'add',
    locales: [
      'en',
      'ru',
    ],
  }),

  computed: {
    ...mapState(['user']),

    timezones() {
      return this.$moment.tz.names();
    },

    headers() {
      const headers = [
        { text: this.$t('common.role'), value: 'role' },
        { text: this.$t('common.name'), value: 'name' },
        { text: this.$t('common.phone'), value: 'phone' },
        { text: 'E-mail', value: 'email' },
        {
          text: this.$t('common.actions'), value: 'actions', sortable: false, width: 150, align: 'center',
        },
      ];

      return headers;
    },
  },

  created() {
    this.getDealer();
  },

  methods: {
    addMissedRoles(rows) {
      const dealerContactRoles = [...new Set(rows.map((o) => o.role))];
      const missedRoles = contactRoles
        .filter((role) => !dealerContactRoles.includes(role))
        .map((role) => ({ role }));

      return [...rows, ...missedRoles];
    },

    async getDealer() {
      const { data } = await this.$http.get(`dealers/${this.asdb || this.user.asdb}`);
      this.dealer = data;
      this.contacts = this.addMissedRoles(data.contacts);
    },

    async save() {
      const params = {
        timezone: this.dealer.timezone,
        language: this.dealer.language,
      };

      this.$http.put(`dealers/${this.asdb || this.user.asdb}`, params);
    },

    showContactsModal(type, contact) {
      this.contactSelected = contact;
      this.modalType = type;
      this.contactsModal = true;
    },

    async removeContact(id) {
      const { data } = await this.$http.delete(`dealers/${this.asdb || this.user.asdb}/contacts/${id}`);
      this.contacts = this.addMissedRoles(data.contacts);
    },

    async contactsModalSubmit() {
      await this.getDealer();
      this.contactsModal = false;
    },
  },
};
</script>

<style lang="scss">
.profile-page .input-block {
  max-width: 350px;
}

.multiselect {
  max-width: 350px;

   ul {
    padding-left: 0;
  }
}
</style>
